import type { BaseAPIResponse } from '~/types/api';
import type { Company, CompanyAdvisory, DeletionGuideResponse, PersonCompanyRelationship } from '~/types/person/companies';
import { ApiError } from '~/utils/errors';

// Pure API service for company operations (non-public) - no state management
export const companiesService = {
    async getPersonCompanyRelationships(personId: number, page: number = 1, per_page: number = 20): Promise<BaseAPIResponse<PersonCompanyRelationship[]>> {
        const nuxtApp = useNuxtApp();
        
        const { data, status, message } = await usePbApi<BaseAPIResponse<PersonCompanyRelationship[]>>(
            `${nuxtApp.$api.routes.people.companies.get_companies(personId)}?page=${page}&per_page=${per_page}`
        );
        
        if (status !== 200 || !data) {
            throw new ApiError(message || 'Failed to fetch companies', status);
        }
        
        if (!data) {
            throw new ApiError('No companies data received', 404);
        }
        
        return data;
    },

    async getCompany(companyId: number): Promise<Company> {
        const nuxtApp = useNuxtApp();
        
        const { data, status, message } = await usePbApi<BaseAPIResponse<Company>>(
            nuxtApp.$api.routes.people.companies.get_company(companyId)
        );
        
        if (status !== 200 || !data) {
            throw new ApiError(message || 'Failed to fetch company', status);
        }
        
        if (!data) {
            throw new ApiError('No company data received', 404);
        }
        
        return data.data;
    },

    async getDeletionGuide(companyId: number): Promise<string> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<DeletionGuideResponse>>(
                nuxtApp.$api.routes.companies.deletion_guide(companyId)
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to fetch deletion guide', status);
            }

            return data.data.guide;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getCompanyAdvisories(companyId: number): Promise<CompanyAdvisory[]> {
        try {
            const nuxtApp = useNuxtApp();
            const { data, status, message } = await usePbApi<BaseAPIResponse<CompanyAdvisory[]>>(
                nuxtApp.$api.routes.public.companies.advisories(companyId)
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to fetch company advisories', status);
            }

            return data.data || [];
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async searchCompanies(query: string, per_page: number = 20, page: number = 1, type: string = ''): Promise<BaseAPIResponse<Company[]>> {
        const nuxtApp = useNuxtApp();
        
        const { data, status, message } = await usePbApi<BaseAPIResponse<Company[]>>(
            nuxtApp.$api.routes.people.companies.search(query, per_page, page, type)
        );
        
        if (status !== 200 || !data) {
            throw new ApiError(message || 'Failed to search companies', status);
        }
        
        return data;
    },

    async requestNewCompany(name: string, url: string): Promise<void> {
        try {
            const { status, message } = await usePbApi('/companies/request', {
                method: 'POST',
                body: {
                    name,
                    url
                }
            });

            if (status !== 201) {
                throw new ApiError(message || 'Failed to submit company request', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getCompanyFaqs(companyId: number): Promise<any[]> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<any[]>>(
                nuxtApp.$api.routes.companies.faqs(companyId)
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to fetch company FAQs', status);
            }

            return data.data || [];
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getCompanyConversation(personId: number, companyId: number): Promise<any[]> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<any[]>>(
                nuxtApp.$api.routes.people.companies.conversation(personId, companyId)
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to fetch company conversation', status);
            }

            return data.data || [];
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getCompanyChannels(personId: number, companyId: number): Promise<any[]> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<any[]>>(
                nuxtApp.$api.routes.people.companies.channels(personId, companyId)
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to fetch company channels', status);
            }

            return data.data || [];
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getCompanyRiskFactors(companyId: number): Promise<any> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<any>>(
                nuxtApp.$api.routes.companies.risk_factors(companyId)
            );

            if (status !== 200 || !data) {
                throw new ApiError(message || 'Failed to fetch company risk factors', status);
            }

            return data.data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async updateChannelOpt(personId: number, companyId: number, channelId: number, opt: boolean): Promise<void> {
        try {
            const { status, message } = await usePbApi(
                `/people/${personId}/companies/${companyId}/channels/${channelId}`,
                {
                    method: 'PATCH',
                    body: { opt }
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to update channel opt', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },
};